// QuestionSetList.js
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Badge
} from "reactstrap";
import { withTranslation } from "react-i18next";

import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';

import { Star, Dot, UserPen, ArrowLeftFromLine } from 'lucide-react';

import './QuestionSetList.css';
import VipBadge from "../../components/VipBadge";
import CardCarousel from './components/QuestionSetListCardCarousel';

import home4 from 'assets/images/info/home4.png';
import placeholder_image from "assets/images/info/card-placeholder-medium.png";

import axiosInstance from "utils/axiosInstance";
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";

import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

const cards = [
  { id: 1, title: "題組型", subtitle: "（有標準答案）", color: "#f9c74f" },
  { id: 2, title: "題組型", subtitle: "（無標準答案）", color: "#f9844a" },
  { id: 3, title: "圖文型", subtitle: "（有標準答案）", color: "#90be6d" },
  { id: 4, title: "圖文型", subtitle: "（無標準答案）", color: "#577590" },
];

const QuestionSetList = ({ t }) => {

  const { topicId } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const { themeName, collectionName, topicName } = location.state || {};

  console.log('themeName:', themeName);
  console.log('collectionName:', collectionName);

  //--------------------------------------------------
  // States
  //--------------------------------------------------

  const [myInfoTopic, setMyInfoTopic] = useState([]);
  const [myInfoQuestionSets, setMyInfoQuestionSets] = useState([]);
  const [collectionThumbnailUrl, setCollectionThumbnailUrl] = useState("");
  const [topicThumbnailUrl, setTopicThumbnailUrl] = useState("");
  const [questionSetThumbnailUrls, setQuestionSetThumbnailUrls] = useState({});

  const [topicLoading, setTopicLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);
  
  const [activeIndex, setActiveIndex] = useState(0);  


  //--------------------------------------------------
  // Load topic and questionSets on mount
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setTopicLoading(true);
      setDataFetchError(null);

      // Load Topic and QuestionSets
      const topicResponse = await axiosInstance.get(
        `/knovia/myInfo/${topicId}/getTopicInfo`
      );
      console.log('Topic Loaded:', topicResponse.data);
      setMyInfoTopic(topicResponse.data);

      const questionSetResponse = await axiosInstance.get(
        `/knovia/myInfo/${topicId}/getQuestionSetsInTopic`
      );
      console.log('QuestionSets in Topic Loaded:', questionSetResponse.data);

      // Sort by `order` before setting state
      const sortedQuestionSets = [...questionSetResponse.data].sort(
        (a, b) => a.order - b.order
      );
      setMyInfoQuestionSets(sortedQuestionSets);

      return {
        topic: topicResponse.data,
        sortedQuestionSets,
      };
    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load data.");
      return null; // <- This ensures the caller knows the fetch failed
    } finally {
      setTopicLoading(false);
    }
  };

  const fetchTopicAndCollectionThumbnail = (topic) => {
    console.log("Start fetching topic and collection thumbnails");
    console.log('topic:', topic);
    fetchThumbnailsWithCache(
      [topic],
      "infoTopic",
      "sizeMedium",
      ({ id, url }) => {
        setTopicThumbnailUrl(prev => url);
      },
      () => {
        console.log("Topic thumbnail finished loading");
      }
    );

    fetchThumbnailsWithCache(
      [topic.infoCollection],
      "infoCollection",
      "sizeMedium",
      ({ id, url }) => {
        setCollectionThumbnailUrl(prev => url);
      },
      () => {
        console.log("Collection image finished loading");
      }
    );
  };


  const fetchQuestionSetThumbnails = (sortedQuestionSets) => {
    console.log("start fetching quesitonSet thumbnails");

    fetchThumbnailsWithCache(
      sortedQuestionSets,
      "infoQuestionSet",
      "sizeMedium",
      ({ id, url }) => {
        setQuestionSetThumbnailUrls(prev => ({
          ...prev,
          [id]: url
        }));
      },
      () => {
        console.log("✅ All thumbnails finished loading");
      }
    );
  };

  useEffect(() => {
    (async () => {
      const { topic, sortedQuestionSets } = await fetchData();
      if (!topic) return;

      if (topic) {
        fetchQuestionSetThumbnails(sortedQuestionSets);
        fetchTopicAndCollectionThumbnail(topic);
      }
    })();
  }, [topicId]);



  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content question-set-list" 
      style={{ "--page-content-background": `url(${home4})` }}
    >

      <Container fluid className="question-set-list-container">

        <div className="question-set-list-breadcrumb">
          <BreadcrumbAboveImage 
            title={t("My Info")}
            breadcrumbItems={[
              { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
              { title: t("..."), to: "/myInfo/navigateThemes" },
              { 
                title: t(`${myInfoTopic?.infoCollection?.theme?.label || themeName || ""}`), 
                to: `/myInfo/themes/${myInfoTopic?.infoCollection?.theme?._id || ""}`
              },
              { 
                title: t(`${myInfoTopic?.infoCollection?.label || collectionName || ""}`), 
                to: `/myInfo/collections/${myInfoTopic?.infoCollection?._id || ""}`
              },
              { title: t(`${myInfoTopic?.label || topicName}`)}
            ]}
          />
          </div>

          <div className="d-block d-md-none">
            {/* Collection Image at top */}
            <div className="question-set-top-image"
              style={{
                backgroundImage: `url(${collectionThumbnailUrl || placeholder_image})`,
              }}
            >
            </div>

            <div className="user-info-topic-page-back-div">
              <Button
                style={{
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: "5px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                  background: "#55535373"
                }}
                onClick={()=>{ navigate(`/myInfo/collections/${myInfoTopic?.infoCollection?._id}`); }}
              >
                <ArrowLeftFromLine size={24} />
              </Button>
            </div>
          </div>

          <Card className="question-set-list-card">
            <CardBody className="question-set-list-card-body">


              {/*------ Small Screen ------*/}
              <div className="d-block d-md-none">

                {/* Topic Thumbnail */}
                <div className="question-set-topic-thumbnail"
                  style={{
                    backgroundImage: `url(${topicThumbnailUrl || placeholder_image})`,
                  }}
                >
                </div>

                {/* Topic Label */}
                <Row>
                  <Col className="question-set-topic-label">
                    <p className="mb-0">{myInfoTopic?.label}</p>
                  </Col>
                </Row>

                {/* Star and VIP level */}
                <Row>
                  <Col className="question-set-topic-star-col">
                    <div className="d-flex">
                      <p className="mb-0">4.9</p>
                      <Star size={18} fill="#faca82" stroke="none"
                        style={{ margin: '1.8px 2px 0px 2px' }}
                      />
                      {/* A filled star */}
                      <p className="mb-0 text-muted">(400+)</p>
                    </div>
                    <div>
                      <Dot size={16} /> {/* You can also use a styled div for a smaller solid dot */}
                    </div>
                    <div style={{ marginTop: "-6px" }}>
                      <VipBadge vipLevel="VIP1" imageSize={35} textSize={42} />
                    </div>
                  </Col>
                </Row>

                <Row style={{ marginBottom: "30px" }}>
                  <Col className="question-set-topic-filled-count-col">
                    <Button color="soft-success" style={{
                      height: "30px",
                      paddingTop: "3.5px"
                    }}>
                      1000+ 人已填寫此主題
                    </Button>
                  </Col>
                </Row>

              </div>


              {/*------ Big Screen ------*/}
              <div className="d-none d-md-block">
                <Row style={{ 
                  paddingTop: "25px",
                  paddingBottom: "25px", 
                  /*backgroundColor: "#f0f0f0",*/
                  background: "linear-gradient(160deg, #91cdff 0%, white 50%)",
                  margin: "0", 
                  borderRadius: "4px 4px 0 0" 
                }}>
                  <Col md={6} className="d-flex justify-content-center">
                    {/* Collection Image at top */}
                    <div className="question-set-top-image"
                      style={{
                        backgroundImage: `url(${collectionThumbnailUrl || placeholder_image})`,
                      }}
                    >
                      {/* Topic Thumbnail */}
                      <div className="question-set-topic-thumbnail"
                        style={{
                          backgroundImage: `url(${topicThumbnailUrl || placeholder_image})`,
                        }}
                      >
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    {/* Topic Label */}
                    <Row>
                      <Col className="d-flex flex-column question-set-topic-label"
                        style={{ padding: "15px 0px 0px 0px" }}
                      >
                        <p style={{ marginBottom: "8px" }}>{myInfoTopic?.label}</p>
                        <div style={{ maxWidth: "70%" }}>
                          <p className="mb-0 text-muted" style={{ fontSize:'13.5px' }}>
                            實用的造型工具，透過加熱金屬棒捲曲頭髮，能創造自然捲度與蓬鬆感，是日常快速整理髮型的好幫手。
                          </p>
                        </div>
                      </Col>
                    </Row>

                    {/* Star and VIP level */}
                    <Row>
                      <Col className="question-set-topic-star-col">
                        <div className="d-flex">
                          <p className="mb-0">4.9</p>
                          <Star size={16} fill="black" strokeWidth="none"
                            style={{ margin: '3px 2px 0px 2px' }}
                          />
                          {/* A filled star */}
                          <p className="mb-0 text-muted">(400+)</p>
                        </div>
                        <div style={{ margin: '0px 2px 0px 5px' }}>
                          <Dot size={16} /> {/* You can also use a styled div for a smaller solid dot */}
                        </div>
                        <div style={{ marginTop: "-6px" }}>
                          <VipBadge vipLevel="VIP1" imageSize={35} textSize={42} />
                        </div>
                      </Col>
                    </Row>

                    <Row style={{ marginBottom: "30px" }}>
                      <Col className="question-set-topic-filled-count-col">
                        <div className="d-flex flex-column flex-lg-row" style={{
                          justifyContent: "space-between", width: "80%"
                        }}>
                          <div className="mb-2 mb-lg-0">
                            <Button color="soft-success" 
                              style={{
                                height: "30px",
                                paddingTop: "3.5px"
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.blur();
                                return false;
                              }}
                            >
                              1000+ 人已填寫此主題
                            </Button>
                          </div>

                          <div className="d-flex question-set-topic-author-div">
                            <UserPen size={24} strokeWidth={1.25} color="#5086b3" />
                            <p className="question-set-topic-author mb-0">TTMY_Admin</p>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>

              </div>




              <div className="section-div" />

              {/*------ Small Screen ------*/}
              <div className="d-block d-md-none">
                {/* QuestionSet Carousel */}
                <Row>
                  <Col className="question-set-topic-filled-carousel-col">
                    <div style={{ paddingLeft: "35px" }}>
                      <p className="mb-0 question-set-section-title">所有題組</p>
                    </div>
                    <CardCarousel 
                      cards={myInfoQuestionSets}
                      activeIndex={activeIndex}
                      setActiveIndex={setActiveIndex}
                    />
                  </Col>
                </Row>

                <div className="section-div" style={{ margin: "30px 0px 0px 0px" }}/>
              </div>


              {/*------ Big Screen ------*/}
              <div className="d-none d-md-block">
                {/* QuestionSet Carousel */}
                <Row style={{ padding: "0px 0px 0px 0px", margin: '0px' }}>
                  <Col className="question-set-topic-filled-carousel-col"
                    style={{ padding: "30px 0px 60px 50px" }}
                  >
                    <div style={{ paddingLeft: "20px" }}>
                      <p className="mb-0 question-set-section-title">所有題組</p>
                    </div>
                    <CardCarousel 
                      cards={myInfoQuestionSets}
                      activeIndex={activeIndex}
                      setActiveIndex={setActiveIndex}
                    />
                  </Col>
                </Row>
              </div>


            </CardBody>
          </Card>

      </Container>
    </div>
  );
};

export default withTranslation()(QuestionSetList);


